import React, { useState, useRef, useEffect } from 'react';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-hcl'; // For Terraform
import '../styles/CodeBlock.css';

function CodeBlock({ children, language = 'javascript', fileName = null }) {
  const [copied, setCopied] = useState(false);
  const codeRef = useRef(null);

  // Handle copy functionality
  const handleCopy = () => {
    if (!codeRef.current) return;
    
    const text = codeRef.current.textContent;
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy code: ', err);
      });
  };

  // Get language display name
  const getLanguageDisplay = () => {
    const langMap = {
      'javascript': 'JavaScript',
      'jsx': 'React JSX',
      'typescript': 'TypeScript',
      'python': 'Python',
      'bash': 'Bash',
      'yaml': 'YAML',
      'json': 'JSON',
      'html': 'HTML',
      'css': 'CSS',
      'hcl': 'Terraform',
    };
    
    return langMap[language] || language.charAt(0).toUpperCase() + language.slice(1);
  };

  // Highlight code on component mount
  useEffect(() => {
    if (codeRef.current) {
      Prism.highlightElement(codeRef.current);
    }
  }, [children, language]);

  return (
    <div className="do-code-block">
      {/* Terminal header */}
      <div className="do-code-header">
        <div className="do-window-buttons">
          <span className="do-window-button do-close"></span>
          <span className="do-window-button do-minimize"></span>
          <span className="do-window-button do-maximize"></span>
        </div>
        <div className="do-code-filename">
          {fileName || getLanguageDisplay()}
        </div>
        <button 
          className={`do-copy-button ${copied ? 'do-copied' : ''}`} 
          onClick={handleCopy}
          aria-label="Copy code"
          title="Copy code">
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      
      {/* Code content */}
      <div className="do-code-content">
        <pre>
          <code ref={codeRef} className={`language-${language}`}>
            {children}
          </code>
        </pre>
      </div>
    </div>
  );
}

export default CodeBlock;
